import React, { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { useHistory } from "react-router";
import PhoneInput from "react-phone-input-2";
import Modal from "@material-ui/core/Modal";
import "react-phone-input-2/lib/style.css";
import { connect } from "react-redux";
import { Actions as ACTIONS } from "store/customer/action";
import { getByPhone } from "middleware/customer";
import { v4 as uuidv4 } from "uuid";
import { format } from "date-fns";
import cleanDeep from "clean-deep";
import DatePicker from "react-datepicker";

import "react-datepicker/dist/react-datepicker.css";
import "styles/datepicker.css";
import CustomerDependentsForm from "./CustomerDependentsForm";

const CustomerForm = ({
  customer,
  customerSearch,
  createCustomer,
  updateCustomer,
  deleteCustomer,
  callback,
  permissions,
  businessID,
  getCustomerByPhone,
}) => {
  let history = useHistory();
  const { register, setValue, handleSubmit, watch, errors } = useForm();
  const [displayDependentsModal, setDisplayDependentsModal] = useState(false);

  register({ name: "phone", type: "custom" }, { required: true }); // disabled required to see what saves
  register({ name: "dob", type: "custom" }, { required: false });
  const phone =
    watch("phone") ||
    (customer && customer?.phone) ||
    (customerSearch &&
    typeof customerSearch == "string" &&
    customerSearch.length == 8
      ? `+356${customerSearch}`
      : customerSearch && typeof customerSearch == "String"
      ? customerSearch
      : "");
  const dob = watch("dob") || (customer?.dob && new Date(customer?.dob));

  useEffect(() => {
    if (customerSearch) {
      const newPhoneValue =
        customerSearch &&
        typeof customerSearch == "string" &&
        customerSearch.length == 8
          ? `+356${customerSearch}`
          : customerSearch;
      setValue("phone", newPhoneValue, { shouldDirty: true });

      setTimeout(() => {
        setValue("phone", newPhoneValue, { shouldDirty: true });
      }, 500);
    }
  }, [customerSearch]);

  useEffect(() => {
    if (customer && customer?.id) {
      setValue("phone", customer?.phone);
      setValue("dob", customer?.dob && new Date(customer?.dob));
    } else {
      setValue("phone", null);
      setValue("dob", null);
    }
  }, [customer && customer?.id]);

  const today = format(new Date(), "yyyy-MM-dd");
  const futureBookings =
    customer &&
    customer?.bookings &&
    customer?.bookings &&
    customer?.bookings.items &&
    customer?.bookings.items.filter(
      (i) => i.date >= today && i.status != "Cancelled"
    );

  const onSubmit = async (data) => {
    if (data.phone.substr(0, 1) !== "+") {
      data.phone = "+" + data.phone;
    }

    let item = {
      id: (customer && customer?.id) || uuidv4(),
      _version: customer && customer?._version,
      givenName: data.givenName,
      familyName: data.familyName,
      phone: data.phone,
      email: data.email,
      vip: data.vip || false,
      phoneVerified: data.phoneVerified || false,
      active: data.active !== false ? true : false, // some older users have this setting as null
    };

    if (data.dob) item.dob = format(data.dob, "yyyy-MM-dd");

    if (customer?.id) {
      updateCustomer(cleanDeep(item), callback);
    } else {
      delete item._version;
      const existingPhone = await verifyPhone(item.phone);
      if (
        existingPhone?.length > 0 &&
        (!existingPhone[0].email || existingPhone[0].email === item.email)
      ) {
        const userConfirmed = window.confirm(
          "A customer with this phone number already exists. Do you wish to overwrite the existing customer?"
        );
        if (userConfirmed) createCustomer(cleanDeep(item), callback);
      } else createCustomer(cleanDeep(item), callback);
    }
  };

  const verifyPhone = async (phone) => {
    const customers = await getCustomerByPhone(phone, businessID);
    return customers;
  };

  const handleCapitalizeName = (fieldName) => (e) => {
    const capitalized = e.target.value
      .split(' ')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' ');
    setValue(fieldName, capitalized, { shouldDirty: true });
  };

  return (
    <div className="mt-5 md:mt-0 md:col-span-2 relative">
      <div
        style={{ position: "absolute", right: "5px", cursor: "pointer" }}
        onClick={() => callback(null)}
      >
        X
      </div>
      <form onSubmit={handleSubmit(onSubmit)}>
        <div className="shadow sm:rounded-md">
          <div className="px-4 py-5 bg-white sm:p-6">
            {customer?.active === false ||
              (customer?._deleted && (
                <div
                  style={{
                    backgroundColor: "red",
                    color: "white",
                    borderRadius: "4px",
                    padding: "5px 10px",
                    marginBottom: "10px",
                  }}
                >
                  This customer is inactive. Please return to the list and
                  search for the phone number to access the active customer.
                </div>
              ))}

            <div className="grid grid-cols-6 gap-6">
              <div className="col-span-6 sm:col-span-3">
                <label
                  htmlFor="givenName"
                  className="block text-sm font-medium leading-5 text-gray-700"
                >
                  First name
                </label>
                <input
                  id="givenName"
                  name="givenName"
                  defaultValue={customer?.givenName}
                  ref={register({ required: true })}
                  onChange={handleCapitalizeName("givenName")}
                  className="mt-1 form-input block w-full py-2 px-3 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:shadow-outline-blue focus:border-blue-300 transition duration-150 ease-in-out sm:text-sm sm:leading-5"
                />
              </div>

              <div className="col-span-6 sm:col-span-3">
                <label
                  htmlFor="familyName"
                  className="block text-sm font-medium leading-5 text-gray-700"
                >
                  Last name
                </label>
                <input
                  id="familyName"
                  name="familyName"
                  defaultValue={customer?.familyName}
                  ref={register}
                  onChange={handleCapitalizeName("familyName")}
                  className="mt-1 form-input block w-full py-2 px-3 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:shadow-outline-blue focus:border-blue-300 transition duration-150 ease-in-out sm:text-sm sm:leading-5"
                />
              </div>

              <div className="col-span-6 sm:col-span-3">
                <label
                  htmlFor="email"
                  className="block text-sm font-medium leading-5 text-gray-700"
                >
                  Email
                </label>
                <input
                  id="email"
                  name="email"
                  defaultValue={customer?.email}
                  ref={register}
                  onChange={(e) => {
                    const lowercased = e.target.value?.toLowerCase();
                    setValue("email", lowercased, { shouldDirty: true });
                    e.target.value = lowercased;
                  }}
                  className="mt-1 form-input block w-full py-2 px-3 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:shadow-outline-blue focus:border-blue-300 transition duration-150 ease-in-out sm:text-sm sm:leading-5"
                />
              </div>

              <div className="col-span-6 sm:col-span-3">
                <label
                  htmlFor="phone"
                  className="block text-sm font-medium leading-5 text-gray-700"
                >
                  Phone
                </label>
                <PhoneInput
                  country={"mt"}
                  value={phone}
                  onChange={(phone) =>
                    setValue("phone", phone, { shouldDirty: true })
                  }
                  enableSearch={true}
                  masks={{ mt: ".... ...." }}
                  inputStyle={{ width: "100%" }}
                />
              </div>

              <div className="col-span-6 sm:col-span-3">
                <label
                  htmlFor="phone"
                  className="block text-sm font-medium leading-5 text-gray-700"
                >
                  Date of Birth
                </label>
                <DatePicker
                  dateFormat="dd/MM/yyyy"
                  selected={dob}
                  onChange={(selectedDate) => {
                    setValue("dob", selectedDate);
                  }}
                  className="mt-1 form-input block w-full py-2 px-3 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:shadow-outline-blue focus:border-blue-300 transition duration-150 ease-in-out sm:text-sm sm:leading-5"
                  popperPlacement="bottom-end"
                  popperModifiers={{
                    offset: {
                      enabled: true,
                    },
                  }}
                />
              </div>

              {permissions == "Admin" && (
                <>
                  <div className="col-span-6 sm:col-span-4">
                    <div className="flex items-start">
                      <div className="absolute flex items-center h-5">
                        <input
                          id="vip"
                          name="vip"
                          ref={register}
                          defaultChecked={customer?.vip}
                          type="checkbox"
                          className="form-checkbox h-4 w-4 text-indigo-600 transition duration-150 ease-in-out"
                        />
                      </div>
                      <div className="pl-7 text-sm leading-5">
                        <label
                          htmlFor="vip"
                          className="font-medium text-gray-700"
                        >
                          VIP
                        </label>
                        <p className="text-gray-500">
                          This client is considered a VIP and entitled to
                          special treatment.
                        </p>
                      </div>
                    </div>
                  </div>

                  <div className="col-span-6 sm:col-span-4">
                    <div className="flex items-start">
                      <div className="absolute flex items-center h-5">
                        <input
                          id="phoneVerified"
                          name="phoneVerified"
                          ref={register}
                          defaultChecked={customer?.phoneVerified}
                          type="checkbox"
                          className="form-checkbox h-4 w-4 text-indigo-600 transition duration-150 ease-in-out"
                        />
                      </div>
                      <div className="pl-7 text-sm leading-5">
                        <label
                          htmlFor="phoneVerified"
                          className="font-medium text-gray-700"
                        >
                          Phone Verified
                        </label>
                        <p className="text-gray-500">
                          Use this box to directly verify or unverify their
                          phone if they cannot verify through the app.
                        </p>
                      </div>
                    </div>
                  </div>

                  <div className="col-span-6 sm:col-span-4">
                    <div className="flex items-start">
                      <div className="absolute flex items-center h-5">
                        <input
                          id="active"
                          name="active"
                          ref={register}
                          defaultChecked={customer?.active !== false}
                          type="checkbox"
                          className="form-checkbox h-4 w-4 text-indigo-600 transition duration-150 ease-in-out"
                        />
                      </div>
                      <div className="pl-7 text-sm leading-5">
                        <label
                          htmlFor="active"
                          className="font-medium text-gray-700"
                        >
                          Enabled
                        </label>
                        <p className="text-gray-500">
                          Uncheck this box to disable the user and prevent new
                          appointments.
                        </p>
                      </div>
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>
          {customer?.active !== false ? (
            <div className="md:flex md:flex-row-reverse bg-gray-50 ">
              <div className="px-4 py-3 bg-gray-50">
                <span className="inline-flex rounded-md shadow-sm">
                  <button
                    disabled={customer?._deleted}
                    type="submit"
                    className="inline-flex justify-center py-2 px-4 border border-transparent text-sm leading-5 font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-500 focus:outline-none focus:border-indigo-700 focus:shadow-outline-indigo active:bg-indigo-700 transition duration-150 ease-in-out"
                  >
                    Save
                  </button>
                </span>
              </div>
              <div className="px-4 py-3">
                {customer?.id && futureBookings?.length == 0 && (
                  <span className="inline-flex rounded-md shadow-sm">
                    <button
                      disabled={customer?._deleted}
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        deleteCustomer(customer.id, history);
                      }}
                      className="inline-flex justify-center py-2 px-4 border border-transparent text-sm leading-5 font-medium rounded-md text-white bg-red-600 hover:bg-red-500 focus:outline-none focus:border-red-700 focus:shadow-outline-red active:bg-red-700 transition duration-150 ease-in-out"
                    >
                      Delete
                    </button>
                  </span>
                )}
              </div>
              {customer?.id && (
                <div className="px-4 py-3 bg-gray-50 ">
                  <span className="inline-flex rounded-md shadow-sm">
                    <button
                      disabled={customer?._deleted}
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        setDisplayDependentsModal(true);
                      }}
                      className="inline-flex justify-center py-2 px-4 border border-transparent text-sm leading-5 font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-500 focus:outline-none focus:border-indigo-700 focus:shadow-outline-indigo active:bg-indigo-700 transition duration-150 ease-in-out"
                    >
                      Add Member
                    </button>
                  </span>
                </div>
              )}
            </div>
          ) : (
            <div className="md:flex p-5 text-red-600">
              {" "}
              This customer is no longer active in the system.
            </div>
          )}
        </div>
      </form>
      {customer?.id && (
        <Modal
          open={displayDependentsModal}
          style={{ position: "fixed" }}
          onClose={() => {
            setDisplayDependentsModal(false);
          }}
        >
          <div>
            <CustomerDependentsForm
              customer={customer}
              updateCustomer={updateCustomer}
              setDisplayDependentsModal={setDisplayDependentsModal}
            />
          </div>
        </Modal>
      )}
    </div>
  );
};

const mapStateToProps = (state) => {
  return {
    customer: state.customer?.item,
    permissions: state.permissions,
    businessID: state.businessID,
  };
};

const mapDispatchToProps = (dispatch) => ({
  createCustomer: (item, callback) =>
    dispatch(ACTIONS.createItem(item, callback)),
  updateCustomer: (item, callback) =>
    dispatch(ACTIONS.updateItem(item, callback)),
  deleteCustomer: (id, history) => dispatch(ACTIONS.deleteItem(id, history)),
  getCustomerByPhone: (phone, businessID) => getByPhone(phone, businessID),
});

export default connect(mapStateToProps, mapDispatchToProps)(CustomerForm);
